/**
 * Auth-Buttons für Sticky-Headers auf der Subdomain.
 *
 * Eingeloggter Customer → "Mein Konto" Button (Avatar mit Initiale).
 * Anonymer Visitor → "Anmelden" + "Registrieren" Buttons (Subdomain-Routes).
 *
 * Wird sowohl im StickyMiniHeader auf der Landing als auch im PdpTopBar
 * auf der Produkt-Detail-Page eingebunden — Customer hat damit auf JEDER
 * Subdomain-Seite Zugriff auf sein Konto.
 */
import { useCustomer } from "../lib/customer_auth";
import { navigate } from "../lib/router";

type ThemeLike = {
  accent: string;
  ink: string;
  inkMuted: string;
  cardBg: string;
};

export function CustomerProfileButton({
  theme,
  isDark,
  isB2bLocked = false,
  registerUrl = null,
  compact = false,
}: {
  theme: ThemeLike;
  isDark: boolean;
  isB2bLocked?: boolean;
  registerUrl?: string | null;
  compact?: boolean;
}) {
  const { customer } = useCustomer();

  // Eingeloggter Customer → Avatar-Button mit Initiale
  if (customer) {
    const initial = (customer.first_name?.[0] || "?").toUpperCase();
    return (
      <button
        onClick={() => navigate("/konto")}
        className="inline-flex items-center gap-1.5 rounded-lg px-2 md:px-3 py-1.5 text-xs font-semibold whitespace-nowrap"
        style={{ background: theme.accent, color: "#fff" }}
        title={`Mein Konto · ${customer.email}`}
        aria-label="Mein Konto"
      >
        <span
          className="grid place-items-center size-5 rounded-full text-[10px] font-bold"
          style={{ background: "rgba(255,255,255,0.25)" }}
        >
          {initial}
        </span>
        {!compact && <span className="hidden sm:inline">Mein Konto</span>}
      </button>
    );
  }

  // Anonymer Visitor bei B2B-Lock → "Anmelden" + "Registrieren"
  if (isB2bLocked) {
    return (
      <>
        <button
          onClick={() => navigate("/login")}
          className="hidden sm:inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold whitespace-nowrap border"
          style={{
            color: theme.ink,
            background: theme.cardBg,
            borderColor: isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.10)",
          }}
        >
          Anmelden
        </button>
        <button
          onClick={() => navigate("/registrieren")}
          className="inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold whitespace-nowrap"
          style={{ background: theme.accent, color: "#fff" }}
        >
          Registrieren →
        </button>
      </>
    );
  }

  // Anonymer Visitor, keine B2B-Lock → "Anmelden"-Button
  return (
    <>
      <button
        onClick={() => navigate("/login")}
        className="inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold whitespace-nowrap"
        style={{ background: theme.accent, color: "#fff" }}
      >
        Anmelden
      </button>
      {registerUrl && !compact && (
        <a
          href={registerUrl}
          className="hidden md:inline-flex items-center text-xs underline whitespace-nowrap"
          style={{ color: theme.inkMuted }}
        >
          Direkt bei dpconnect.de →
        </a>
      )}
    </>
  );
}
